<template>
  <div class="cd-widget">
    <div v-if="loading" class="text-center py-4">
      <v-progress-circular indeterminate size="28" color="green darken-1" />
    </div>
    <div v-else-if="fetchError" class="text-center py-4">
      <v-icon color="grey lighten-1" size="32">mdi-alert-circle-outline</v-icon>
      <p class="text-caption grey--text mt-2">Unable to load data</p>
    </div>
    <div v-else class="kpi-card-body">
      <div class="kpi-gradient-bg kpi-gradient-green" />

      <div class="d-flex align-center justify-space-between mb-4">
        <div class="kpi-icon-wrap kpi-icon-green">
          <v-icon size="32" :color="toneColor('green')">{{ icons.mdiCashRegister }}</v-icon>
        </div>
        <div class="text-right">
          <div class="kpi-number" :class="toneText('green')">
            {{ formatCurrency(grandTotal) }}
          </div>
          <div class="text-caption grey--text font-weight-medium">
            {{ grandCount }} transaction(s)
          </div>
        </div>
      </div>

      <div class="d-flex align-center mb-3">
        <v-icon x-small color="grey lighten-1" class="mr-1">{{ icons.mdiCalendar }}</v-icon>
        <span class="text-caption grey--text">{{ displayDate }}</span>
      </div>

      <div v-if="breakdown.length > 0">
        <v-divider class="mb-3" />
        <div v-for="item in breakdown" :key="item.type" class="mb-2">
          <div class="d-flex align-center justify-space-between mb-1">
            <div class="d-flex align-center">
              <v-icon x-small :color="typeColor(item.type)" class="mr-1">
                {{ typeIcon(item.type) }}
              </v-icon>
              <span class="text-caption text-capitalize">{{ item.type }}</span>
            </div>
            <span
              class="text-caption font-weight-bold"
              :style="`color:${typeColorHex(item.type)}`"
            >
              {{ formatCurrency(item.total) }}
            </span>
          </div>
          <v-progress-linear
            :value="grandTotal > 0 ? (item.total / grandTotal) * 100 : 0"
            :color="typeColor(item.type)"
            height="6" rounded :background-color="isDark ? 'grey darken-3' : 'grey lighten-4'"
          />
        </div>
      </div>
      <div v-else class="text-caption grey--text text-center py-2">
        No transactions for this date
      </div>
    </div>
  </div>
</template>

<script>
import {
  mdiCashRegister,
  mdiCalendar,
  mdiCash,
  mdiCheckbook,
  mdiCreditCard,
  mdiBank,
  mdiSwapHorizontal,
} from '@mdi/js';
import { clients } from '../../../util/clients';
import WidgetThemeMixin from '../WidgetTheme.mixin';
import { toStandardDate } from '../../../util/shared/tmc-global';

const { backendRest } = clients.direct;

/* `color` is the Vuetify class for the icon and the progress bar - a filled bar, so the saturated
   shade is right on either theme. `hex` / `hexDark` are the amount text beside it, which has to
   flip: #388e3c on #1e1e1e is about 2.6:1, well under the minimum for a bold 0.75rem figure. */
const TYPE_CONFIG = {
  cash: { color: 'green darken-1', hex: '#388e3c', hexDark: '#81c784' },
  check: { color: 'blue darken-1', hex: '#1976d2', hexDark: '#64b5f6' },
  credit: { color: 'purple darken-1', hex: '#7b1fa2', hexDark: '#ce93d8' },
  debit: { color: 'indigo darken-1', hex: '#303f9f', hexDark: '#9fa8da' },
  ach: { color: 'teal darken-1', hex: '#00796b', hexDark: '#4db6ac' },
  wire: { color: 'cyan darken-1', hex: '#0097a7', hexDark: '#4dd0e1' },
};

export default {
  name: 'CashDrawerWidget',
  mixins: [WidgetThemeMixin],
  data: () => ({
    loading: true,
    fetchError: false,
    grandTotal: 0,
    grandCount: 0,
    breakdown: [],
    displayDate: '',
    icons: {
      mdiCashRegister,
      mdiCalendar,
      mdiCash,
      mdiCheckbook,
      mdiCreditCard,
      mdiBank,
      mdiSwapHorizontal,
    },
  }),
  async created() {
    await this.fetchData();
  },
  methods: {
    async fetchData() {
      this.loading = true;
      try {
        const today = new Date().toISOString().substring(0, 10);
        const response = await backendRest.get('/dashboard/cash-drawer', {
          params: { businessDate: today },
        });
        const data = response.data || {};
        this.grandTotal = data.grandTotal || 0;
        this.grandCount = data.grandCount || 0;
        this.breakdown = data.breakdown || [];
        this.displayDate = data.businessDate
          ? toStandardDate(data.businessDate)
          : toStandardDate(today);
      } catch (e) {
        console.error('CashDrawerWidget fetch failed', e);
        this.fetchError = true;
        this.grandTotal = 0;
        this.grandCount = 0;
        this.breakdown = [];
        this.displayDate = '';
      } finally {
        this.loading = false;
      }
    },
    formatCurrency(val) {
      const n = val || 0;
      return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    },
    typeColor(type) {
      const cfg = TYPE_CONFIG[(type || '').toLowerCase()];
      return cfg ? cfg.color : 'grey darken-1';
    },
    typeColorHex(type) {
      const cfg = TYPE_CONFIG[(type || '').toLowerCase()];
      if (!cfg) return this.isDark ? '#bdbdbd' : '#757575';
      return this.isDark ? cfg.hexDark : cfg.hex;
    },
    typeIcon(type) {
      const key = (type || '').toLowerCase();
      if (key === 'cash') return this.icons.mdiCash;
      if (key === 'check') return this.icons.mdiCheckbook;
      if (key === 'credit' || key === 'credit card') return this.icons.mdiCreditCard;
      if (key === 'ach' || key === 'wire') return this.icons.mdiBank;
      return this.icons.mdiSwapHorizontal;
    },
    async refresh() {
      await this.fetchData();
    },
  },
};
</script>

<style scoped>
.cd-widget { position: relative; }
.kpi-card-body { padding: 4px 0; position: relative; }
.kpi-gradient-bg {
  position: absolute; top: -20px; right: -20px;
  width: 120px; height: 120px; border-radius: 50%;
  opacity: 0.18; pointer-events: none;
}
.kpi-gradient-green { background: radial-gradient(circle, #43a047 0%, transparent 70%); }
.kpi-icon-wrap {
  width: 64px; height: 64px; border-radius: 16px;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.kpi-icon-green {
  background: var(--widget-tint-ok, linear-gradient(135deg, #e8f5e9, #c8e6c9));
  box-shadow: 0 4px 16px rgba(67, 160, 71, 0.2);
}
.kpi-number {
  font-size: 2rem; font-weight: 800;
  line-height: 1; letter-spacing: -1px;
}
</style>
